import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiToast } from '@hooks/useApiToast';
import { enterpriseApiClient } from '../../client';
import { WorkOrdersApi } from '../../openapi';
import type { WorkOrderChargeViewModel } from '../../openapi';
import { workOrderKeys } from '../constants';
import { db } from '@db/database';
import { enqueue, processAll } from '@services/offlineSyncService';

type DeleteWorkOrderChargeParams = {
  jobNumber: string;
  sequenceNumber: number;
};

type MutationContext = {
  previousCharges: WorkOrderChargeViewModel[] | undefined;
};

export const useDeleteWorkOrderCharge = (onSuccessCallback?: () => void) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useApiToast();
  const workOrdersApi = new WorkOrdersApi(
    undefined,
    undefined,
    enterpriseApiClient
  );

  return useMutation<void, Error, DeleteWorkOrderChargeParams, MutationContext>(
    {
      networkMode: 'always',
      mutationFn: async ({ jobNumber, sequenceNumber }) => {
        if (!navigator.onLine) {
          await db.cachedCharges
            .where('[jobNumber+sequenceNumber]')
            .equals([jobNumber, sequenceNumber])
            .delete();

          await enqueue({
            entityType: 'charge_delete',
            entityKey: `${jobNumber}:${sequenceNumber}`,
            payload: { jobNumber, sequenceNumber },
            priority: 50
          });

          return;
        }

        await workOrdersApi.apiWorkOrdersJobNumberChargesSequenceNumberDelete({
          jobNumber,
          sequenceNumber
        });
      },
      onMutate: async ({ jobNumber, sequenceNumber }) => {
        await queryClient.cancelQueries({
          queryKey: workOrderKeys.charges(jobNumber)
        });

        const previousCharges = queryClient.getQueryData<
          WorkOrderChargeViewModel[]
        >(workOrderKeys.charges(jobNumber));

        queryClient.setQueryData<WorkOrderChargeViewModel[]>(
          workOrderKeys.charges(jobNumber),
          old => {
            if (!old) return old;
            return old.filter(
              charge => charge.sequenceNumber !== sequenceNumber
            );
          }
        );

        return { previousCharges };
      },
      onSuccess: async (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: workOrderKeys.charges(variables.jobNumber)
        });
        queryClient.invalidateQueries({
          queryKey: workOrderKeys.byJobNumber(variables.jobNumber)
        });
        showSuccess('deleteChargeModal.success', 'charges');
        onSuccessCallback?.();

        if (!navigator.onLine) {
          processAll();
        }

        if (navigator.onLine) {
          try {
            const metadata = await db.downloadMetadata.get(
              String(variables.jobNumber)
            );
            if (metadata?.status === 'cached') {
              await db.cachedCharges
                .where('[jobNumber+sequenceNumber]')
                .equals([String(variables.jobNumber), variables.sequenceNumber])
                .delete();
            }
          } catch {
            // Best effort — online React Query cache is still fresh
          }
        }
      },
      onError: (error, variables, context) => {
        if (context?.previousCharges) {
          queryClient.setQueryData(
            workOrderKeys.charges(variables.jobNumber),
            context.previousCharges
          );
        }
        showError('deleteChargeModal.error', error, 'charges');
      }
    }
  );
};
